import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { SolutionDataWithId } from '../types';
import { getHistory, clearApiHistory, deleteMultipleHistoryItems } from '../services/apiService';
import Button from './Button';
import ShareSection from './ShareSection';
import SolutionDisplay from './SolutionDisplay';
import ImageWithModal from './ImageWithModal';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import TrashIcon from './icons/TrashIcon';
import ListBulletIcon from './icons/ListBulletIcon';
import LoadingSpinner from './LoadingSpinner';
import { useTranslation } from 'react-i18next';

interface ProblemHistoryViewProps {
  onGoToSolve: () => void;
  requestConfirmation: (title: string, message: string, onConfirm: () => void) => void;
}

const ProblemHistoryView: React.FC<ProblemHistoryViewProps> = ({ onGoToSolve, requestConfirmation }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<SolutionDataWithId[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const observer = useRef<IntersectionObserver>();

  const toggleExpand = (id: string) => {
    if (isSelectionMode) return;
    setExpandedId(expandedId === id ? null : id);
  };

  const handleToggleSelection = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const fetchMoreHistory = useCallback(async () => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);
    try {
      const { items, hasMore: newHasMore } = await getHistory(page, 10);
      setHistory(prev => [...prev, ...items]);
      setPage(prev => prev + 1);
      setHasMore(newHasMore);
    } catch (error) {
      console.error("Failed to fetch more history items", error);
      toast.error(t('problemHistoryView.loadMoreError'));
    } finally {
      setIsFetchingMore(false);
    }
  }, [page, hasMore, isFetchingMore]);

  const lastItemRef = useCallback(node => {
    if (isFetchingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchMoreHistory();
      }
    });
    if (node) observer.current.observe(node);
  }, [isFetchingMore, hasMore, fetchMoreHistory]);

  useEffect(() => {
    setIsLoading(true);
    getHistory(0, 10).then(({ items, hasMore: newHasMore }) => {
      setHistory(items);
      setHasMore(newHasMore);
      setPage(1);
    }).catch(error => {
      console.error("Failed to fetch initial history", error);
      toast.error(t('problemHistoryView.initialLoadError'));
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  const handleSelectAll = () => {
    if (selectedIds.length === history.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(history.map(item => item.id));
    }
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedIds([]);
  };

  const handleDeleteSelected = () => {
    requestConfirmation(
      t('problemHistoryView.deleteSelectedTitle', { count: selectedIds.length }),
      t('problemHistoryView.deleteSelectedMessage'),
      async () => {
        try {
          await deleteMultipleHistoryItems(selectedIds);
          setHistory(prev => prev.filter(item => !selectedIds.includes(item.id)));
          toast.success(t('problemHistoryView.deleteSelectedSuccess'));
        } catch (error) {
          toast.error(t('problemHistoryView.deleteSelectedError'));
        } finally {
          setIsSelectionMode(false);
          setSelectedIds([]);
        }
      }
    );
  };

  const handleClearHistory = () => {
    requestConfirmation(
      t('problemHistoryView.clearAllTitle'),
      t('problemHistoryView.clearAllMessage'),
      async () => {
        try {
          await clearApiHistory();
          setHistory([]);
          setHasMore(false);
          toast.success(t('problemHistoryView.clearAllSuccess'));
        } catch (error) {
          toast.error(t('problemHistoryView.clearAllError'));
        }
      }
    );
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('sr-RS', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-16">
        <LoadingSpinner />
        <p className="ml-4 text-slate-600">{t('problemHistoryView.loadingSolutions')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 sm:p-6 md:p-8 shadow-xl rounded-lg">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h2 className="text-2xl md:text-3xl font-bold text-sky-700 print:hidden">{t('problemHistoryView.title')}</h2>
        {history.length > 0 && (
          <div className="flex gap-2 print:hidden">
            {isSelectionMode ? (
              <>
                <Button onClick={handleCancelSelection} variant="secondary" size="sm">{t('problemHistoryView.cancel')}</Button>
                <Button onClick={handleDeleteSelected} variant="danger" size="sm" disabled={selectedIds.length === 0}>
                  <TrashIcon className="w-4 h-4 mr-1.5" />
                  {t('problemHistoryView.deleteCount', { count: selectedIds.length })}
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsSelectionMode(true)} variant="ghost" size="sm">{t('problemHistoryView.select')}</Button>
            )}
          </div>
        )}
      </div>

      {history.length === 0 ? (
        <div className="text-center py-16 px-6 bg-slate-50 rounded-lg print:hidden">
          <ListBulletIcon className="w-12 h-12 mx-auto text-slate-400" />
          <h3 className="mt-4 text-lg font-semibold text-slate-700">{t('problemHistoryView.noSolutionsTitle')}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {t('problemHistoryView.noSolutionsDescription')}
          </p>
          <Button onClick={onGoToSolve} className="mt-6">{t('problemHistoryView.solveFirstProblem')}</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {isSelectionMode && (
            <div className="flex items-center p-2 border-b border-slate-200 print:hidden">
              <input
                type="checkbox"
                checked={history.length > 0 && selectedIds.length === history.length}
                onChange={handleSelectAll}
                id="select-all"
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
              />
              <label htmlFor="select-all" className="ml-2 text-sm font-medium text-slate-600">
                {t('problemHistoryView.selectAll')}
              </label>
            </div>
          )}
            {history.map((item, index) => {
              const title = item.solution_jsonb?.math_solutions?.[0]?.problem_title_generated || t('problemHistoryView.problemFromDate', { date: formatDate(item.timestamp) });
              const grade = item.solution_jsonb?.math_solutions?.[0]?.problem_grade || item.grade;
              const isExpanded = expandedId === item.id;
              const isLastElement = history.length === index + 1;

              return (
                <div key={item.id} ref={isLastElement ? lastItemRef : null} className={`border rounded-lg shadow-sm transition-all duration-300 ${isSelectionMode ? 'bg-sky-50/50 border-sky-200' : 'bg-white'} print:shadow-none print:border-none ${!isExpanded ? 'print:hidden' : ''}`}>
                  <div
                    className={`flex items-center p-3 gap-3 ${!isSelectionMode ? 'cursor-pointer hover:bg-slate-50' : ''} rounded-t-lg print:hidden`}
                    onClick={() => toggleExpand(item.id)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && toggleExpand(item.id)}
                  >
                    {isSelectionMode && (
                      <div className="flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => handleToggleSelection(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                          aria-label={t('problemHistoryView.selectProblem', { title })}
                        />
                      </div>
                    )}
                    <div className="flex-shrink-0">
                      <ImageWithModal src={item.originalImageSrc} alt="Problem thumbnail" className="w-12 h-12 object-cover rounded-md border border-slate-200" />
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{title}</p>
                      <p className="text-xs text-slate-500">{t('problemHistoryView.grade')}: {grade} | {t('problemHistoryView.solved')}: {new Date(item.timestamp * 1000).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: '2-digit' })}</p>
                    </div>
                    {!isSelectionMode && (
                      <button
                        className="p-2 focus:outline-none focus:ring-2 focus:ring-sky-300 rounded-full flex-shrink-0"
                        aria-expanded={isExpanded}
                        aria-controls={`solution-${item.id}`}
                      >
                        {isExpanded ? <ChevronUpIcon className="w-6 h-6 text-sky-600" /> : <ChevronDownIcon className="w-6 h-6 text-slate-500" />}
                      </button>
                    )}
                  </div>
                  {isExpanded && !isSelectionMode && (
                    <div id={`solution-${item.id}`} className="p-4 border-t border-slate-200 bg-white rounded-b-lg print:p-0 print:border-none">
                      <div id={`printable-history-item-${item.id}`}><SolutionDisplay solution={item} /></div>
                      <ShareSection 
                        solutionId={item.id} 
                        className="mt-6 pt-4 border-t border-slate-300 print:hidden"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          {isFetchingMore && (
            <div className="flex justify-center items-center p-4 print:hidden">
              <LoadingSpinner />
              <p className="ml-2 text-slate-500">{t('problemHistoryView.loadingMore')}</p>
            </div>
          )}
          {!isSelectionMode && history.length > 0 && (
            <div className="text-center pt-6 border-t border-slate-200 mt-6 print:hidden">
              <Button onClick={handleClearHistory} variant="secondary" className="text-red-600 hover:bg-red-50 hover:border-red-200">
                <TrashIcon className="w-4 h-4 mr-2" />
                {t('problemHistoryView.clearAllTitle')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProblemHistoryView;